/**
 * State Action Buttons Component
 * 
 * Renders next action buttons based on server-provided nextEvents.
 * Strictly advisory - server remains authoritative.
 */

import React from 'react';
import { Button } from '@etherisc/ui-kit';

export interface StateActionButtonsProps {
  nextEvents: string[];
  onTransition: (event: string, payload?: unknown) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}

export interface EventConfig {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger';
  requiresConfirmation?: boolean;
  payload?: unknown;
}

// Event configuration mapping
const EVENT_CONFIGS: Record<string, EventConfig> = {
  UPDATE: {
    label: 'Update',
    variant: 'secondary'
  },
  APPROVE: {
    label: 'Approve',
    variant: 'primary',
    requiresConfirmation: true
  },
  // REJECT removed in v2 - quotes are not rejected, they stay in 'new' state
  READY_FOR_COLLECTION: {
    label: 'Ready for Collection',
    variant: 'primary'
  },
  PAY_FULL: {
    label: 'Pay Full Amount',
    variant: 'primary',
    requiresConfirmation: true
  },
  PAY_PARTIAL: {
    label: 'Pay Partial Amount',
    variant: 'secondary'
  },
  PAY_REMAINING: {
    label: 'Pay Remaining Amount',
    variant: 'primary'
  },
  COLLECTION_EXPIRED: {
    label: 'Mark as Expired',
    variant: 'danger',
    requiresConfirmation: true
  }
};

export const StateActionButtons: React.FC<StateActionButtonsProps> = ({
  nextEvents,
  onTransition,
  loading = false,
  disabled = false,
  className = ''
}) => {
  const handleTransition = async (event: string, config: EventConfig) => {
    if (config.requiresConfirmation) {
      const confirmed = window.confirm(`Are you sure you want to ${config.label.toLowerCase()}?`);
      if (!confirmed) {
        return;
      }
    }

    try {
      await onTransition(event, config.payload);
    } catch (error) {
      console.error(`Failed to execute ${event}:`, error);
      // Error handling could be enhanced with toast notifications
    }
  };

  if (nextEvents.length === 0) {
    return (
      <div className={`text-gray-500 text-sm ${className}`}>
        No actions available
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {nextEvents.map((event) => {
        const config = EVENT_CONFIGS[event] || {
          label: event.toLowerCase().replace(/_/g, ' '),
          variant: 'secondary' as const
        };

        return (
          <Button
            key={event}
            variant={config.variant}
            onClick={() => handleTransition(event, config)}
            disabled={loading || disabled}
            className="capitalize"
          >
            {loading ? 'Processing...' : config.label}
          </Button>
        );
      })}
    </div>
  );
};
